import { faSave } from '@fortawesome/free-solid-svg-icons'
import moment from 'moment'
import React, { useEffect, useState } from 'react'
import {
  Badge,
  Button,
  Col,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Spinner
} from 'reactstrap'
import {
  DELETION_SOURCE_LABELS,
  DELETION_STATUS_COLORS,
  DELETION_STATUS_LABELS,
  updateAccountDeletionStatus
} from '../../../../helpers/api/accountDeletion'
import { SUCCESS_RESPONSE } from '../../../../helpers/codeResponses/codes'
import handleErrorMessage from '../../../../helpers/errors/handleErrorMessage'
import ButtonIcon from '../../../common/ButtonIcon'
import { Toast } from '../../../UI/toast/Toast'

// El estado "unverified" lo asigna el backend cuando el solicitante todavía no
// confirmó desde su correo, por eso no se ofrece como opción manual.
const MANAGEABLE_STATUS = ['pending', 'in_process', 'completed', 'rejected']

const Field = ({ label, children, sm = 6 }) => (
  <Col sm={sm} className="mb-3">
    <p className="text-600 fs--2 mb-1 text-uppercase">{label}</p>
    <p className="mb-0">{children || 'No indicado'}</p>
  </Col>
)

const AccountDeletionDetailsModal = ({
  isOpen,
  setIsOpen,
  request,
  setSelected,
  mutate = () => {}
}) => {
  const [submitting, setSubmitting] = useState(false)
  const [status, setStatus] = useState('pending')
  const [adminNotes, setAdminNotes] = useState('')

  useEffect(() => {
    if (request) {
      setStatus(
        MANAGEABLE_STATUS.includes(request.status) ? request.status : 'pending'
      )
      setAdminNotes(request.admin_notes || '')
    }
  }, [request])

  const toggle = () => {
    setIsOpen(!isOpen)
    setSelected(null)
  }

  const save = () => {
    setSubmitting(true)
    updateAccountDeletionStatus(request.id, status, adminNotes || null)
      .then(({ code }) => {
        if (code === SUCCESS_RESPONSE.OK) {
          Toast.fire({
            type: 'success',
            title: 'Solicitud actualizada exitosamente.'
          })
          mutate()
          toggle()
        }
      })
      .catch((error) => handleErrorMessage(error))
      .finally(() => setSubmitting(false))
  }

  const closeBtn = (
    <Button
      className="close font-weight-normal bg-transparent border-0 shadow-none"
      onClick={toggle}
    >
      &times;
    </Button>
  )

  if (!request) return null

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg" centered>
      <ModalHeader toggle={toggle} close={closeBtn}>
        Solicitud de eliminación #{request.id}
      </ModalHeader>
      <ModalBody className="fs--1">
        <Row>
          <Field label="Solicitante">{request.full_name}</Field>
          <Field label="Estado actual">
            <Badge color={`soft-${DELETION_STATUS_COLORS[request.status]}`} className="fs--2">
              {DELETION_STATUS_LABELS[request.status] || request.status}
            </Badge>
          </Field>
          <Field label="Correo electrónico">
            {request.email || 'Sin correo registrado'}
          </Field>
          <Field label="Nombre de usuario">{request.username}</Field>
          <Field label="Teléfono">{request.phone}</Field>
          <Field label="Documento de identidad">{request.dni}</Field>
          <Field label="Condominio">{request.condominium_name}</Field>
          <Field label="Origen">
            {DELETION_SOURCE_LABELS[request.source] || request.source}
          </Field>
          <Field label="Cuenta en el sistema">
            {request.has_account ? 'Sí, enlazada' : 'No se encontró una cuenta con ese correo'}
          </Field>
          <Field label="Confirmada por el solicitante">
            {request.verified_at
              ? moment(request.verified_at).format('DD/MM/YYYY HH:mm')
              : 'Aún sin confirmar'}
          </Field>
          <Field label="Motivo" sm={12}>
            {request.reason}
          </Field>
          <Field label="Recibida">
            {moment(request.created_at).format('DD/MM/YYYY HH:mm')}
          </Field>
          <Field label="Última gestión">
            {request.processed_at
              ? `${moment(request.processed_at).format('DD/MM/YYYY HH:mm')}${
                  request.processed_by ? ` — ${request.processed_by}` : ''
                }`
              : 'Sin gestionar'}
          </Field>
        </Row>

        <hr />

        <Row>
          <FormGroup tag={Col} sm={6}>
            <Label>ESTADO</Label>
            <Input
              type="select"
              value={status}
              onChange={({ target }) => setStatus(target.value)}
            >
              {MANAGEABLE_STATUS.map((value) => (
                <option key={value} value={value}>
                  {DELETION_STATUS_LABELS[value]}
                </option>
              ))}
            </Input>
          </FormGroup>
          <FormGroup tag={Col} sm={12}>
            <Label>NOTA INTERNA</Label>
            <Input
              type="textarea"
              rows={3}
              value={adminNotes}
              placeholder="Deja constancia de qué se hizo con esta solicitud"
              onChange={({ target }) => setAdminNotes(target.value)}
            />
          </FormGroup>
        </Row>
      </ModalBody>
      <ModalFooter>
        <Button color="falcon-default" onClick={toggle} disabled={submitting}>
          Cerrar
        </Button>
        <ButtonIcon icon={faSave} color="primary" onClick={save} disabled={submitting}>
          {submitting ? <Spinner size="sm" /> : 'Guardar'}
        </ButtonIcon>
      </ModalFooter>
    </Modal>
  )
}

export default AccountDeletionDetailsModal
